// Per-vertical creative bridge (#02): turn a vertical's gallery JSON into the
// DemoPost[] + DemoProduct map that WidgetDemoPreview renders, so each industry
// page's live preview shows vertical-matched content (travel = trip reels +
// stays, beauty = routines, etc.) instead of generic fashion demo data.
//
// Source fields: the gallery tiles already carry `pill` ("Product · $price"),
// `cap` ("@creator"), `caption`, and an image keyword `kw`; the `player`
// widget carries `kw` + `tags` (["Name · $price", ...]).
import type { DemoPost, DemoProduct } from '@/lib/copy/demo-products'
import { unsplashPlaceholderFor, guessCategoryFromText } from '@/lib/copy/placeholder-images'
import { galleryForIndustrySlug, type GalleryTile } from '@/lib/copy/widget-galleries'

function parsePill(pill?: string): { name: string; price: string } | null {
  if (!pill) return null
  const parts = pill.split('·').map((s) => s.trim())
  if (parts.length >= 2 && parts[1]) return { name: parts[0], price: parts[1] }
  return null
}

function handle(cap?: string): string {
  const h = (cap || 'creator').trim().replace(/^@+/, '')
  return `@${h}`
}

// Small deterministic spread so a vertical's numbers don't all match.
function vary(base: number, seed: number, span: number): number {
  return base + ((seed * 37) % span)
}

const LOCAL_PRODUCT_IMAGE_OVERRIDES: Record<string, string> = {
  'home-lifestyle:bliss cleanview compact turbo':
    '/demo/products/bliss-clean-view-compact-turbo.webp',
}

export interface GalleryDemo {
  posts: DemoPost[]
  products: Record<string, DemoProduct>
}

type LocalSlide = { image: string; videoSrc?: string }
function videoSlide(src: string): LocalSlide {
  return { image: src, videoSrc: src }
}
const LOCAL_MEDIA_OVERRIDES: Record<string, LocalSlide[]> = {
  'accessories-and-jewelery': [
    videoSlide('/demo/accessories-and-jewelery/accessories-and-jewelery-slide-1.webm'),
    videoSlide('/demo/accessories-and-jewelery/accessories-and-jewelery-slide-2.webm'),
    videoSlide('/demo/accessories-and-jewelery/accessories-and-jewelery-silide-3.webm'),
    videoSlide('/demo/accessories-and-jewelery/accessories-and-jewelery-slide-4.webm'),
  ],
  'beauty-and-cosmetics': [
    videoSlide('/demo/beauty-and-cosmetics/beauty-and-cosmetics-slide-1.webm'),
    videoSlide('/demo/beauty-and-cosmetics/beauty-and-cosmetics-slide-2.webm'),
    videoSlide('/demo/beauty-and-cosmetics/beauty-and-cosmetics-slide-3.webm'),
  ],
  'fashion-and-apparels': [
    videoSlide('/demo/fashion-and-apparels/fashion-and-apparels-slide-1.webm'),
    videoSlide('/demo/fashion-and-apparels/fashion-and-apparels-slide-2.webm'),
    { image: '/demo/fashion-and-apparels/fashion-and-apparels-slide-3.jpg' },
  ],
  furniture: [
    videoSlide('/demo/furniture/furniture-slide-1.webm'),
    videoSlide('/demo/furniture/furniture-slide-2.webm'),
  ],
  'home-lifestyle': [videoSlide('/demo/home-lifestyle/home-lifestyle-slide-1.webm')],
}

export function galleryDemoPostsFor(industrySlug: string): GalleryDemo {
  const gallery = galleryForIndustrySlug(industrySlug)
  if (!gallery) return { posts: [], products: {} }

  const products: Record<string, DemoProduct> = {}
  const posts: DemoPost[] = []
  const seen = new Set<string>()
  let seed = 1

  // Candidate tiles: any tile that carries a product pill, across all sections,
  // plus each shoppable `player` widget (image + first tagged product).
  const candidates: GalleryTile[] = []
  for (const section of gallery.sections) {
    for (const w of section.widgets) {
      if (Array.isArray(w.tiles)) {
        for (const t of w.tiles) {
          if (typeof t !== 'string' && t.pill) candidates.push(t)
        }
      }
      if (w.type === 'player' && w.kw && Array.isArray(w.tags) && w.tags[0]) {
        candidates.push({ kw: w.kw, cap: w.source, caption: w.title, pill: w.tags[0] })
      }
    }
  }

  for (const t of candidates) {
    const parsed = parsePill(t.pill)
    if (!parsed) continue
    const key = `${t.kw}|${parsed.name}`
    if (seen.has(key)) continue
    seen.add(key)

    const id = `gal-${industrySlug}-${seed}`
    const cat = guessCategoryFromText(`${t.kw} ${parsed.name}`)
    const productImageOverride =
      LOCAL_PRODUCT_IMAGE_OVERRIDES[`${industrySlug}:${parsed.name.toLowerCase()}`]
    products[id] = {
      id,
      name: parsed.name,
      brand: gallery.name,
      price: parsed.price,
      currency: '$',
      image:
        productImageOverride ??
        unsplashPlaceholderFor(`${parsed.name}-${seed}`, {
          category: cat,
          width: 600,
          height: 600,
        }),
      blurb: t.caption || `${parsed.name}, shared by ${gallery.name.toLowerCase()} customers.`,
      rating: 4.8,
      reviews: vary(120, seed, 400),
    }
    posts.push({
      image: unsplashPlaceholderFor(`${t.kw}-${seed}`, { category: cat, width: 800, height: 1000 }),
      caption: t.caption || `Tagged in this ${gallery.noun} post.`,
      user: handle(t.cap),
      likes: vary(800, seed, 5000),
      comments: vary(20, seed, 180),
      hotspots: [{ x: vary(34, seed, 28), y: vary(40, seed, 30), productId: id }],
    })

    seed++
    if (posts.length >= 6) break
  }

  const mediaOverrides = LOCAL_MEDIA_OVERRIDES[industrySlug]
  if (mediaOverrides) {
    mediaOverrides.forEach((slide, idx) => {
      if (posts[idx]) {
        posts[idx].image = slide.image
        posts[idx].videoSrc = slide.videoSrc
      }
    })

    posts.length = Math.min(posts.length, mediaOverrides.length)
  }

  return { posts, products }
}
